// ─── What: the shared hover furniture ─────────────────────────────────────────
//
// Appended AFTER the brush and edge glow so it sits above the plot, and before
// the signal / trade / alert overlays so those stay above it. Every panel drives
// the same crosshair; see the note in drawContext.ts.

import * as d3 from 'd3';
import { alpha } from '@mui/material/styles';
import { MARGIN } from '../layout';
import type { ChartChrome, ChartGroup, DrawCtx } from '../drawContext';

export function createChrome(
    ctx: DrawCtx,
    tooltipEl: HTMLDivElement | null
): ChartChrome {
    const { svg, theme, colors, layout } = ctx;

    const crossV = svg
        .append('line')
        .attr('stroke', alpha(colors.textColor, 0.3))
        .attr('stroke-width', 0.5)
        .attr('stroke-dasharray', '3,3')
        .style('display', 'none')
        .style('pointer-events', 'none');

    const hoverDots = svg
        .append('g')
        .attr('class', 'hover-dots')
        .style('pointer-events', 'none')
        .style('display', 'none') as ChartGroup;

    // TradingView-style floating axis labels. Width is set at hover time from
    // the measured text, so the rect is created empty here.
    const makePill = (): ChartGroup => {
        const g = svg
            .append('g')
            .style('pointer-events', 'none')
            .style('display', 'none') as ChartGroup;
        g.append('rect')
            .attr('rx', 2)
            .attr('height', 15)
            .attr('fill', theme.palette.primary.main);
        g.append('text')
            .attr('fill', theme.palette.primary.contrastText)
            .attr('font-size', '10px')
            .attr('font-family', 'monospace')
            .attr('font-weight', 700)
            .attr('dominant-baseline', 'middle');
        return g;
    };
    const pricePill = makePill();
    const timePill = makePill();

    const tooltip = d3.select(tooltipEl);

    return {
        crossV,
        hoverDots,
        pricePill,
        timePill,
        tooltip,
        showCross(mx: number) {
            crossV
                .attr('x1', mx)
                .attr('y1', MARGIN.top)
                .attr('x2', mx)
                .attr('y2', layout.totalHeight)
                .style('display', null);
        },
        hideCross() {
            crossV.style('display', 'none');
        },
        hideTooltip() {
            tooltip.style('display', 'none');
        },
        suppressForMarker() {
            crossV.style('display', 'none');
            hoverDots.style('display', 'none');
            tooltip.style('display', 'none');
        },
        hideAll() {
            crossV.style('display', 'none');
            hoverDots.style('display', 'none');
            tooltip.style('display', 'none');
            pricePill.style('display', 'none');
            timePill.style('display', 'none');
        },
    };
}
